import { toast } from "@/v2-ui/toast"
import { useTranslationRevertMutation } from "@/v2-backoffice-client/translation/pending/translation.revert.mutation"
import type { PendingChangeRow } from "@/v2-backoffice-client/translation/pending/translation.pending.utils"

export type UseTranslationPendingRevertInput = {
  /** Fired after a revert lands, so the board can refetch. */
  onReverted?(): void
}

/**
 * Reverts pending changelog lines back to their previously published text.
 *
 * Shared by the per-row button and the selection-toolbar button: the
 * mutation reports per-item success, so partial failure has to be summarised
 * rather than thrown, and both surfaces must summarise it the same way. Each
 * caller gets its own mutation instance, so only the button that was pressed
 * shows a spinner.
 */
export function useTranslationPendingRevert(
  input?: UseTranslationPendingRevertInput,
) {
  const { onReverted } = input ?? {}

  const [revert, { loading }] = useTranslationRevertMutation()

  async function revertRows(rows: PendingChangeRow[]) {
    try {
      const res = await revert({
        variables: {
          items: rows.map((row) => ({
            ObjectType: row.objectType,
            ObjectKey: row.code,
            LanguageCode: row.languageCode,
            OldName: row.revertName,
            OldDescription: row.revertDescription,
          })),
        },
      })

      const results = res.data?.revertTranslations ?? []
      const failed = results.filter((result) => !result.Ok)

      if (failed.length === 0) {
        toast({
          title: `Reverted ${results.length} change(s) to the previously published text.`,
        })
      } else {
        toast({
          variant: "destructive",
          title: `Reverted ${results.length - failed.length} change(s), ${failed.length} failed`,
          description: `${failed[0].ObjectKey} (${failed[0].LanguageCode}): ${failed[0].Error ?? "unknown error"}`,
        })
      }

      onReverted?.()
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Revert failed",
        description: error instanceof Error ? error.message : String(error),
      })
    }
  }

  return { revertRows, loading }
}
